import React, { useState, useEffect } from 'react';
import { Lock, Shield, Server, FileCheck, ShieldAlert } from 'lucide-react';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { Badge } from '../components/ui';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const SecurityPage = () => {
  const brandName = 'OrgSets';
  const proseClasses = "prose prose-lg max-w-none leading-relaxed prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white";

  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'data-protection', 'infrastructure', 'compliance'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'overview', label: '1. Overview' },
    { id: 'data-protection', label: '2. Data Protection' },
    { id: 'infrastructure', label: '3. Infrastructure' },
    { id: 'compliance', label: '4. Compliance' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Security" description={`Learn about the enterprise-grade security and data protection measures at ${brandName}.`} />
        <div className="max-w-6xl mx-auto">
          
          <LegalHero 
            title="Security"
            subtitle="Enterprise-Grade Defense Protocols. We utilize a defense-in-depth strategy to protect your organizational data from ever-evolving threats."
            icon={<ShieldAlert size={14} />}
            label="Trust & Defense"
            version="All Systems Secure"
            updated="Active"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            {/* Sticky Navigation */}
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className="font-bold uppercase text-[10px] tracking-widest font-mono text-slate-400 dark:text-slate-500">
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="overview" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-none bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. Security Overview</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Security is woven into every layer of our stack. We utilize a defense-in-depth strategy to protect your organizational data from ever-evolving threats. Our approach ensures that even if one defense mechanism fails, others are in place to thwart attacks.
                  </p>
                </div>
              </section>

              <section id="data-protection" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-none bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <Lock size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. Data Protection</h2>
                </div>
                <div className={proseClasses}>
                  <div className="rounded-none p-8 my-8 border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] bg-white border-white dark:bg-[#111] dark:border-white/10">
                    <h4 className="font-bold mb-4 text-slate-900 dark:text-white">
                      Encryption Standard
                    </h4>
                    <p className="m-0">
                      All client data is encrypted using <strong>AES-256</strong> at rest and <strong>TLS 1.3</strong> in transit. We enforce strict data isolation between tenants to ensure that one organization's data can never be accessed by another.
                    </p>
                  </div>
                </div>
              </section>

              <section id="infrastructure" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-none bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <Server size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Infrastructure</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Our platform runs on globally distributed edge networks with built-in DDoS protection and Web Application Firewalls (WAF). We conduct regular automated vulnerability scanning and periodic third-party penetration testing.
                  </p>
                  <ul className="list-disc pl-5 mt-4 marker:text-brand-primary">
                    <li>DDoS Mitigation at the edge</li>
                    <li>Automated dependency vulnerability scanning</li>
                    <li>Strict IAM role isolation for internal services</li>
                  </ul>
                </div>
              </section>

              <section id="compliance" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-none bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <FileCheck size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. Compliance</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We maintain compliance with major security standards, including SOC 2 Type II readiness and GDPR data protection requirements. Our infrastructure partners are fully certified across all major compliance regimes (ISO 27001, PCI-DSS, etc).
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
